/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Email Dashboard Page
 * ============================================================================
 *
 * Dashboard for monitoring email delivery statistics and viewing sent emails.
 * Shows delivery metrics, email logs, and template information.
 *
 * ============================================================================
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Mail,
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Filter,
  Send,
  AlertCircle,
} from 'lucide-react';
import {
  useEmailStats,
  useEmails,
  useEmailTypes,
} from '@/hooks/api/useEmails';
import { EmailStatus, EmailType } from '@/services/superadmin/emails.service';
import { formatDistanceToNow } from 'date-fns';
import { SearchInput } from '@/components/ui';

export function EmailDashboardPage() {
  const { t } = useTranslation('platform');
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState<EmailStatus | undefined>(undefined);
  const [typeFilter, setTypeFilter] = useState<EmailType | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState('');

  const { data: stats, isLoading: statsLoading, refetch: refetchStats } = useEmailStats();
  const { data: emails, isLoading: emailsLoading, refetch: refetchEmails } = useEmails(page, 20, {
    status: statusFilter,
    type: typeFilter,
    search: searchQuery || undefined,
  });
  const { data: emailTypes } = useEmailTypes();

  const getStatusIcon = (status: EmailStatus) => {
    switch (status) {
      case 'SENT':
        return <CheckCircle className="status-icon status-sent" />;
      case 'FAILED':
        return <XCircle className="status-icon status-failed" />;
      case 'QUEUED':
        return <Clock className="status-icon status-queued" />;
      default:
        return <AlertCircle className="status-icon" />;
    }
  };

  const handleRefresh = () => {
    refetchStats();
    refetchEmails();
  };

  if (statsLoading) {
    return (
      <div className="page-container">
        <div className="loading-state">
          <RefreshCw className="animate-spin" size={24} />
          <span>{t('common.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div className="header-content">
          <h1 className="page-title">
            <Mail className="title-icon" />
            {t('emails.title')}
          </h1>
          <div className="header-actions">
            <button className="btn-secondary" onClick={handleRefresh}>
              <RefreshCw size={16} />
              {t('common.refresh')}
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon total">
              <Send size={20} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{stats.last24h.total}</span>
              <span className="stat-label">{t('emails.stats.totalSent')}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon success">
              <CheckCircle size={20} />
            </div>
            <div className="stat-content">
              <span className="stat-value success-value">{stats.last24h.sent}</span>
              <span className="stat-label">{t('emails.stats.delivered')}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon error">
              <XCircle size={20} />
            </div>
            <div className="stat-content">
              <span className="stat-value error-value">{stats.last24h.failed}</span>
              <span className="stat-label">{t('emails.stats.failed')}</span>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon queued">
              <Clock size={20} />
            </div>
            <div className="stat-content">
              <span className="stat-value">{stats.last24h.queued}</span>
              <span className="stat-label">{t('emails.stats.queued')}</span>
            </div>
          </div>
          <div className="stat-card wide">
            <div className="stat-icon success">
              <CheckCircle size={20} />
            </div>
            <div className="stat-content">
              <span className="stat-value success-value">{stats.last24h.deliveryRate.toFixed(1)}%</span>
              <span className="stat-label">{t('emails.stats.deliveryRate')}</span>
            </div>
          </div>
        </div>
      )}

      {/* Top Email Types */}
      {stats && stats.topTypes.length > 0 && (
        <div className="section">
          <h2 className="section-title">{t('emails.topTypes')}</h2>
          <div className="types-grid">
            {stats.topTypes.map((type, index) => (
              <div key={type.type} className="type-card">
                <span className="type-rank">#{index + 1}</span>
                <span className="type-name">{type.type}</span>
                <span className="type-count">{type.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <Filter size={16} />
          <select
            className="filter-select"
            value={statusFilter || ''}
            onChange={(e) => {
              setStatusFilter(e.target.value as EmailStatus || undefined);
              setPage(0);
            }}
          >
            <option value="">{t('emails.allStatuses')}</option>
            <option value="SENT">{t('emails.status.sent')}</option>
            <option value="FAILED">{t('emails.status.failed')}</option>
            <option value="QUEUED">{t('emails.status.queued')}</option>
          </select>
        </div>
        <div className="filter-group">
          <select
            className="filter-select"
            value={typeFilter || ''}
            onChange={(e) => {
              setTypeFilter(e.target.value as EmailType || undefined);
              setPage(0);
            }}
          >
            <option value="">{t('emails.allTypes')}</option>
            {emailTypes?.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder={t('emails.searchPlaceholder')}
          className="search-group"
        />
      </div>

      {/* Email Logs Table */}
      <div className="section">
        <h2 className="section-title">{t('emails.recentEmails')}</h2>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('emails.columns.recipient')}</th>
                <th>{t('emails.columns.subject')}</th>
                <th>{t('emails.columns.type')}</th>
                <th>{t('emails.columns.status')}</th>
                <th>{t('emails.columns.sentAt')}</th>
              </tr>
            </thead>
            <tbody>
              {emails?.content.map((email) => (
                <tr key={email.id}>
                  <td>
                    <span className="recipient-email">{email.recipientEmail}</span>
                  </td>
                  <td>
                    <span className="subject-text" title={email.subject}>
                      {email.subject}
                    </span>
                  </td>
                  <td>
                    {email.emailType && (
                      <span className="type-badge">{email.emailType}</span>
                    )}
                  </td>
                  <td>
                    <div className="status-cell">
                      {getStatusIcon(email.status)}
                      <span className={`status-text status-${email.status.toLowerCase()}`}>
                        {t(`emails.status.${email.status.toLowerCase()}`)}
                      </span>
                    </div>
                    {email.errorMessage && (
                      <span className="error-message" title={email.errorMessage}>
                        {email.errorMessage}
                      </span>
                    )}
                  </td>
                  <td className="time-cell">
                    {email.sentAt
                      ? formatDistanceToNow(new Date(email.sentAt), { addSuffix: true })
                      : email.createdAt
                        ? formatDistanceToNow(new Date(email.createdAt), { addSuffix: true })
                        : '-'}
                  </td>
                </tr>
              ))}
              {emailsLoading && (
                <tr>
                  <td colSpan={5} className="loading-cell">
                    <RefreshCw className="animate-spin" size={16} />
                    {t('common.loading')}
                  </td>
                </tr>
              )}
              {!emailsLoading && (!emails || emails.content.length === 0) && (
                <tr>
                  <td colSpan={5} className="empty-state">
                    {t('emails.noEmails')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {emails && emails.totalPages > 1 && (
          <div className="pagination">
            <button
              className="btn-secondary"
              disabled={emails.first}
              onClick={() => setPage(p => p - 1)}
            >
              {t('common.previous')}
            </button>
            <span className="page-info">
              {emails.number + 1} / {emails.totalPages}
            </span>
            <button
              className="btn-secondary"
              disabled={emails.last}
              onClick={() => setPage(p => p + 1)}
            >
              {t('common.next')}
            </button>
          </div>
        )}
      </div>

      <style>{pageStyles}</style>
    </div>
  );
}

const pageStyles = `
  .page-container {
    padding: 24px;
    max-width: 1400px;
    margin: 0 auto;
  }

  .page-header {
    margin-bottom: 24px;
  }

  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .page-title {
    font-size: 24px;
    font-weight: 600;
    color: var(--color-text-primary);
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .title-icon {
    color: var(--color-brand);
  }

  .header-actions {
    display: flex;
    gap: 8px;
  }

  .btn-secondary {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 16px;
    border: 1px solid var(--color-border);
    background: var(--color-bg-secondary);
    color: var(--color-text-primary);
    border-radius: 6px;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.15s ease;
  }

  .btn-secondary:hover:not(:disabled) {
    background: var(--color-bg-hover);
  }

  .btn-secondary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* Stats Grid */
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 16px;
    margin-bottom: 32px;
  }

  .stat-card {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    padding: 16px;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .stat-card.wide {
    grid-column: span 1;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: var(--color-bg-tertiary, #F3F4F6);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-text-secondary);
  }

  .stat-icon.total { background: #DBEAFE; color: #1D4ED8; }
  .stat-icon.success { background: #D1FAE5; color: #047857; }
  .stat-icon.error { background: #FEE2E2; color: #B91C1C; }
  .stat-icon.queued { background: #FEF3C7; color: #B45309; }

  .stat-content {
    display: flex;
    flex-direction: column;
  }

  .stat-value {
    font-size: 20px;
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .stat-value.success-value { color: var(--color-success, #10B981); }
  .stat-value.error-value { color: var(--color-error, #DC2626); }

  .stat-label {
    font-size: 12px;
    color: var(--color-text-secondary);
  }

  /* Section */
  .section {
    margin-bottom: 32px;
  }

  .section-title {
    font-size: 16px;
    font-weight: 600;
    color: var(--color-text-primary);
    margin-bottom: 16px;
  }

  /* Types Grid */
  .types-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 12px;
  }

  .type-card {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 6px;
    padding: 12px 16px;
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .type-rank {
    font-size: 11px;
    font-weight: 600;
    color: var(--color-brand);
    background: var(--color-brand-subtle, #EEF2FF);
    padding: 2px 6px;
    border-radius: 4px;
  }

  .type-name {
    flex: 1;
    font-size: 13px;
    font-weight: 500;
  }

  .type-count {
    font-size: 14px;
    font-weight: 600;
    color: var(--color-text-secondary);
  }

  /* Filters */
  .filters-section {
    margin-bottom: 24px;
    display: flex;
    gap: 16px;
    flex-wrap: wrap;
  }

  .filter-group {
    display: flex;
    align-items: center;
    gap: 8px;
    color: var(--color-text-secondary);
  }

  .filter-select {
    padding: 8px 12px;
    border: 1px solid var(--color-border);
    background: var(--color-bg-secondary);
    color: var(--color-text-primary);
    border-radius: 6px;
    font-size: 13px;
    min-width: 140px;
  }

  .search-group {
    flex: 1;
    min-width: 200px;
  }

  /* Table */
  .table-container {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: 8px;
    overflow: hidden;
  }

  .data-table {
    width: 100%;
    border-collapse: collapse;
  }

  .data-table th {
    text-align: left;
    padding: 12px 16px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    color: var(--color-text-secondary);
    background: var(--color-bg-tertiary, #F9FAFB);
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .data-table td {
    padding: 12px 16px;
    font-size: 13px;
    color: var(--color-text-primary);
    border-bottom: 1px solid var(--color-border-subtle);
  }

  .data-table tr:last-child td {
    border-bottom: none;
  }

  .data-table tr:hover {
    background: var(--color-bg-hover);
  }

  .recipient-email {
    font-family: monospace;
    font-size: 12px;
  }

  .subject-text {
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: block;
  }

  .type-badge {
    display: inline-block;
    padding: 2px 8px;
    background: var(--color-bg-tertiary, #F3F4F6);
    border-radius: 4px;
    font-size: 10px;
    text-transform: uppercase;
  }

  .status-cell {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .status-icon {
    width: 16px;
    height: 16px;
  }

  .status-icon.status-sent { color: var(--color-success, #10B981); }
  .status-icon.status-failed { color: var(--color-error, #DC2626); }
  .status-icon.status-queued { color: var(--color-warning, #F59E0B); }

  .status-text {
    font-size: 12px;
  }

  .status-text.status-sent { color: var(--color-success, #10B981); }
  .status-text.status-failed { color: var(--color-error, #DC2626); }
  .status-text.status-queued { color: var(--color-warning, #F59E0B); }

  .error-message {
    display: block;
    font-size: 10px;
    color: var(--color-error, #DC2626);
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-top: 4px;
  }

  .time-cell {
    color: var(--color-text-secondary);
    font-size: 12px;
  }

  .loading-cell {
    text-align: center;
    color: var(--color-text-muted);
    padding: 24px 16px !important;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .empty-state {
    text-align: center;
    color: var(--color-text-muted);
    padding: 40px 16px !important;
  }

  /* Pagination */
  .pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;
    margin-top: 16px;
    padding: 16px;
  }

  .page-info {
    font-size: 13px;
    color: var(--color-text-secondary);
  }

  /* Loading/Error States */
  .loading-state,
  .error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: 60px;
    color: var(--color-text-secondary);
  }

  .error-state {
    color: var(--color-error, #DC2626);
  }

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  .animate-spin {
    animation: spin 1s linear infinite;
  }

  @media (max-width: 768px) {
    .page-container {
      padding: 16px;
    }

    .header-content {
      flex-direction: column;
      align-items: flex-start;
      gap: 16px;
    }

    .stats-grid {
      grid-template-columns: repeat(2, 1fr);
    }

    .filters-section {
      flex-direction: column;
    }

    .filter-select,
    .search-input {
      width: 100%;
    }
  }
`;

export default EmailDashboardPage;
